import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Paper, Typography, CircularProgress, Button } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import StaticCharts from "../containers/Charts/StaticCharts";
import WeightGainChart from "../containers/Charts/WeightGainChart";
import StaticChartSmall from "../containers/Charts/StaticChartSmall";
import SleepChart from "../containers/Charts/SleepChart";
import SugarIntakeChart from "../containers/Charts/SugarIntakeChart";
import WaterIntakeChart from "../containers/Charts/WaterIntakeChart";
import Scrollbar from "../containers/Anatomy/Scrollbar";
import BPChart from "../containers/Observation/BPChart";
import { useSelector, useDispatch } from "react-redux";
import { store } from "../redux/store";
import * as patientActions from "../redux/actions/patientActions";
import Pulse from "../containers/Observation/Pulse";
import Temperature from "../containers/Observation/Temp";
import Spo2 from "../containers/Observation/Spo2";
import SmokingChart from "../containers/Charts/SmokingChart";
import VisitsChart from "../containers/Charts/VisitsChart";
import StressChart from "../containers/Charts/StressChart";
import FetalMovementGraph from "../containers/Charts/DFMR";
import Modal from '@mui/material/Modal';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import CloseIcon from '@mui/icons-material/Close';
import AntenatalChart from "../containers/Charts/Tests";
import PrescriptionChart from "../containers/Charts/PrescriptionChart";
import LineChartWeek from "../containers/Charts/LineChart";
import TubeChartWrapper from "../containers/Charts/TubeChartWrapper";


function mergeAnswersWithSchedules(item) {

  const answers = Array.isArray(item?.answers) ? item.answers : [];
  const schedules = Array.isArray(item?.consultationSchedules)
    ? item.consultationSchedules
    : [];

  const answersMap = new Map(
    answers.map((a) => [
      Number(a.assessmentWeek),
      { ...a, week: Number(a.assessmentWeek) },
    ])
  );

  const mergedAnswers = schedules.map((s) => {
    const wk = Number(s.week);
    if (answersMap.has(wk)) {
      return answersMap.get(wk); // already normalized to have "week"
    }
    return {
      week: wk,
      trimester: s.trimester,
      answer: null,
      minValue: 0,
      maxValue: 10,
    };
  });

  return { ...item, answers: mergedAnswers };
}

function mergeWeightData(idealWeightGain = [], weights = []) {
  const weightMap = new Map(weights?.map((w) => [w.week, w]));

  const merged = idealWeightGain?.map((ideal) => {
    const actual = weightMap.get(ideal.week);
    return {
      week: ideal.week,
      trimester: ideal.trimester,
      min: ideal.min,
      max: ideal.max,
      idealUnit: ideal.unit,
      weight: actual ? actual.weight : null,
      weightUnit: actual ? actual.unit : null,
      weightId: actual ? actual.id : null,
      createdAt: actual ? actual.createdAt : null,
    };
  });

  weights.forEach((actual) => {
    if (!merged.some((m) => m.week === actual.week)) {
      merged.push({
        week: actual.week,
        trimester: actual.trimester,
        min: null,
        max: null,
        idealUnit: null,
        weight: actual.weight,
        weightUnit: actual.unit,
        weightId: actual.id,
        createdAt: actual.createdAt,
      });
    }
  });

  return merged.sort((a, b) => a.week - b.week);
}

const SummaryPage = () => {
  const { patientId } = useParams();

  const [stressLevel, setStressLevel] = React.useState("low-stress");

  const patientReducer = useSelector((state) => state.patientReducer);
  const vitals = patientReducer?.vitals;
  const vitalsRange = patientReducer?.vitalsRange;
  const lifestyleGraphs = patientReducer?.lifestyleGraphs;
  const visitsChart = patientReducer?.visitsChart;
  const emotionCharts = patientReducer?.stressChart;
  const stressChart = mergeAnswersWithSchedules(emotionCharts?.[0]);
  const weightGainChart = patientReducer?.weightGainChart;
  const weightGainChartData = mergeWeightData(
    weightGainChart?.idealWeightGain,
    weightGainChart?.weights
  );

  const prescriptionChart = patientReducer?.prescriptionChart
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const useScrollContainer = !isPhone;
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(null)
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false)
    setType(null)
  };

  useEffect(() => {
    if (patientId) {
      store.dispatch(patientActions.getPatientVitalsRequestAction(patientId));
      store.dispatch(
        patientActions.getPatientVitalsRangeRequestAction(patientId)
      );
      store.dispatch(patientActions.getLifestyleChartsRequestAction(patientId));
      store.dispatch(patientActions.getVisitsChartsRequestAction(patientId));
      store.dispatch(patientActions.getEmotionChartsRequestAction(patientId));
      store.dispatch(patientActions.getWeightGainChartRequestAction(patientId));
      store.dispatch(patientActions.getPrescriptionChartRequestionAction(patientId));
      store.dispatch(patientActions.getDFMRChartRequestAction(patientId));
    }
  }, [patientId]);

  if (patientReducer.loading) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }


  const sleepData = lifestyleGraphs?.find((item) => item.graphLabel === "SLEEP");

  const waterData = lifestyleGraphs?.find(
    (item) => item.graphLabel === "WATER_INTAKE"
  );

  const sugarData = lifestyleGraphs?.find(
    (item) => item.graphLabel === "SUGAR_INTAKE"
  );

  const smokingData = lifestyleGraphs?.find((item) => item.graphLabel === "SMOKING");

  const sleepChartData = mergeAnswersWithSchedules(
    lifestyleGraphs?.find((item) => item.graphLabel === "SLEEP")
  );
  const waterChartData = mergeAnswersWithSchedules(
    lifestyleGraphs?.find((item) => item.graphLabel === "WATER_INTAKE")
  );
  const sugarChartData = mergeAnswersWithSchedules(
    lifestyleGraphs?.find((item) => item.graphLabel === "SUGAR_INTAKE")
  );
  const smokingChartData = mergeAnswersWithSchedules(
    lifestyleGraphs?.find((item) => item.graphLabel === "SMOKING")
  );

  const chartPaperSx = (mobileHeight, desktopHeight) => ({
    display: "flex",
    height: { xs: mobileHeight, sm: desktopHeight },
    width: { xs: "100%", lg: "auto" },
    backgroundColor: "rgba(256, 256, 256, 0.3)",
    m: { xs: 0.5, sm: 0.75, lg: 1 },
    borderRadius: { xs: 3, lg: 5 },
    p: 1,
    boxSizing: "border-box",
    overflow: "hidden",
    minWidth: 0,
    ...(isPhone ? { position: "relative" } : {}),
  });

  const expandIconButtonSx = {
    right: 20,
    cursor: "pointer",
    fontSize: "medium",
  };

  const expandIconMobileSx = {
    position: "absolute",
    top: 8,
    right: 8,
    cursor: "pointer",
    fontSize: "medium",
    zIndex: 1,
  };

  const renderExpandIcon = (onClick) =>
    isPhone ? (
      <OpenWithIcon onClick={onClick} sx={expandIconMobileSx} />
    ) : (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignContent: "space-between" }}>
        <OpenWithIcon onClick={onClick} sx={expandIconButtonSx} />
      </Box>
    );

  const columnSx = {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    width: { xs: "100%", sm: "33%" },
    minWidth: 0,
  };

  const getModalHeight = () => {
    if (isPhone) return "auto";
    if (type === "Tests") return "33%";
    if (type === "Prescription") return "40%";
    return "45%";
  };

  const summaryContent = (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: { xs: "column", sm: "row" },
        width: { xs: "100%", lg: "79vw", xl: "83vw" },
        maxWidth: "100%",
        boxSizing: "border-box",
        pr: isTablet ? 0.5 : 0,
        gap: isTablet ? 0.5 : 0,
        pb: useScrollContainer ? 1 : 0,
      }}
    >
      <Box sx={columnSx}>
        <Paper
          elevation={3}
          sx={{
            ...chartPaperSx(360, "353px"),
            flexDirection: "column",
          }}
        >
          <WeightGainChart data={weightGainChartData} />
        </Paper>
        <Paper elevation={3} sx={chartPaperSx(180, "160px")}>
          <LineChartWeek title={sleepData?.graphLabel} idealValue={sleepData?.recommendation} data={sleepData?.values} />
        </Paper>
        <Paper elevation={3} sx={chartPaperSx(200, "200px")}>
          <FetalMovementGraph handleOpen={handleOpen} records={patientReducer?.dfmrChart}
            settingType={(data) => setType(data)} title="DFMR" />
        </Paper>
      </Box>
      <Box sx={columnSx}>
        <Paper elevation={3} sx={chartPaperSx(180, "160px")}>
          <BPChart
            data={vitals}
            systolicProjection={vitalsRange?.systolicBp || []}
            diastolicProjection={vitalsRange?.diastolicBp || []}
          />
          {renderExpandIcon(() => {
            handleOpen()
            setType("BP")
          })}
        </Paper>
        <Paper elevation={3} sx={chartPaperSx(180, "160px")}>
          <Pulse data={vitals} projection={vitalsRange?.pulse || []} />
          {renderExpandIcon(() => {
            handleOpen()
            setType("Pulse")
          })}
        </Paper>
        <Paper elevation={3} sx={chartPaperSx(180, "160px")}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              width: "100%",
              minWidth: 0,
              fontSize: "8px",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignContent: "space-between" }}>
              <Typography
                align="left"
                sx={{ marginLeft: 2, fontSize: "15px", fontWeight: "bold" }}
              >
                Emotion
              </Typography>
              {emotionCharts?.length > 0 && <OpenWithIcon onClick={() => {
                handleOpen()
                setType("emotion")
              }} sx={expandIconButtonSx} />}
            </Box>
            <StressChart stressChart={emotionCharts} page="Summary" handleOpen={handleOpen} />
          </Box>
        </Paper>
        <Paper elevation={3} sx={chartPaperSx(180, "160px")}>
          <LineChartWeek title={waterData?.graphLabel} idealValue={waterData?.recommendation} data={waterData?.values} />
        </Paper>
      </Box>
      <Box sx={columnSx}>
        <Paper elevation={3} sx={chartPaperSx(180, "160px")}>
          <Temperature
            data={vitals}
            projection={vitalsRange?.temperaturs || []}
          />
          {renderExpandIcon(() => {
            handleOpen()
            setType("Temp")
          })}
        </Paper>
        <Paper elevation={3} sx={chartPaperSx(180, "160px")}>
          <Spo2 data={vitals} projection={vitalsRange?.spo2 || []} />
          {renderExpandIcon(() => {
            handleOpen()
            setType("spo2")
          })}
        </Paper>
        <Paper elevation={3} sx={chartPaperSx(140, "96px")}>
          <VisitsChart visitsChartData={visitsChart} page={"Summary"} title={"Visits"} handleOpen={handleOpen} />
          {renderExpandIcon(() => {
            handleOpen()
            setType("visit")
          })}
        </Paper>
        <Paper elevation={3} sx={chartPaperSx(140, "96px")}>
          <AntenatalChart name={"Tests"} handleOpen={handleOpen} settingType={(data) => setType(data)} data={visitsChart} />
        </Paper>
        <Paper elevation={3} sx={chartPaperSx(140, "96px")}>
          <PrescriptionChart name={"Prescription"} handleOpen={handleOpen} settingType={(data) => setType(data)} data={prescriptionChart} />
        </Paper>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: isPhone
          ? 'auto'
          : isTablet
            ? 'calc(100vh - 100px)'
            : '88vh',
        maxHeight: isPhone ? 'none' : isTablet ? 'calc(100vh - 100px)' : '88vh',
        mb: 2,
        px: { xs: 1, sm: 1.5, lg: 2 },
        py: { xs: 1, xl: 1.5, sm: 1 },
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          backgroundColor: "#E0F3F9",
          borderRadius: 4,
          boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
          border: "1px solid rgba(211,211,211, 0.10)",
          overflow: useScrollContainer ? 'hidden' : 'visible',
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          height: useScrollContainer ? '100%' : 'auto',
          width: "100%",
        }}
      >
        {useScrollContainer ? (
          <Scrollbar
            style={{ height: "100%", minHeight: 0, flex: 1 }}
            gridTemplate="auto / 1fr 20px"
            padding="0 0rem"
            scrollerPadding="0rem"
            scrollerWidth="12px"
          >
            {summaryContent}
          </Scrollbar>
        ) : (
          summaryContent
        )}
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            sx={{
              bgcolor: "#fff",
              p: isPhone ? 1.5 : 2.5,
              mx: "auto",
              mt: isPhone ? "2vh" : "10vh",
              width: isPhone ? "96%" : "90%",
              maxWidth: 800,
              maxHeight: isPhone ? "96vh" : "none",
              height: getModalHeight(),
              minHeight: isPhone ? "55vh" : undefined,
              overflowY: isPhone ? "auto" : undefined,
              borderRadius: isPhone ? 2 : 10,
              boxSizing: "border-box",
            }}
          >
            <CloseIcon onClick={() => handleClose()} sx={{ cursor: 'pointer', alignSelf: 'flex-end', display: 'flex', justifySelf: 'flex-end', }} />
            {
              type == "Nutrition" ? <Box sx={{
                display: "flex",
                height: { xs: "70vh", sm: "98%" },
                width: "98%",
              }}> <StaticCharts title={"Nutrition"} /> </Box> :
                type == "Physical" ? <StaticCharts title={"Physical"} /> :
                  type == "Tests" ? <AntenatalChart name={"Tests"} data={visitsChart} />
                    :
                    type == "Prescription" ? <Box sx={{
                      display: "flex",
                      height: { xs: "70vh", sm: "95%" },
                      width: "98%",
                    }}><PrescriptionChart name={"Prescription"} data={prescriptionChart} /></Box>
                      :
                      type == sugarData?.graphLabel ? <SugarIntakeChart
                        sugarChartData={sugarChartData?.answers}
                        page={"Summary"}
                        title={sugarData?.graphLabel}
                        idealValue={sugarData?.recommendation}
                      /> :
                        type == "DFMR" ? <Box sx={{
                          display: "flex",
                          height: { xs: "70vh", sm: "98%" },
                          width: "98%",
                        }}>
                          <FetalMovementGraph title="DFMR" records={patientReducer?.dfmrChart} />
                        </Box> :
                          type == "BP" ? <Box sx={{
                            display: "flex",
                            height: { xs: "70vh", sm: "98%" },
                            width: "98%",
                          }}>
                            <BPChart
                              data={vitals}
                              systolicProjection={vitalsRange?.systolicBp || []}
                              diastolicProjection={vitalsRange?.diastolicBp || []}
                            />
                          </Box>
                            :
                            type == "Pulse" ?
                              <Box sx={{
                                display: "flex",
                                height: { xs: "70vh", sm: "98%" }, width: "98%",
                              }}><Pulse data={vitals} projection={vitalsRange?.pulse || []} /></Box>

                              : type == "Temp" ? <Box sx={{
                                display: "flex",
                                height: { xs: "70vh", sm: "98%" }, width: "98%",
                              }}>
                                <Temperature
                                  data={vitals}
                                  projection={vitalsRange?.temperaturs || []}
                                />
                              </Box> :
                                type == "spo2" ?
                                  <Box sx={{
                                    display: "flex",
                                    height: { xs: "70vh", sm: "98%" }, width: "98%",
                                  }}>
                                    <Spo2 data={vitals} projection={vitalsRange?.spo2 || []} />
                                  </Box> :
                                  type == "visit" ?
                                    <Box sx={{
                                      display: "flex",
                                      height: { xs: "70vh", sm: "98%" },
                                      width: "98%",
                                    }}>
                                      <VisitsChart visitsChartData={visitsChart} page={"Summary"} title={"Visits"} />
                                    </Box>
                                    :
                                    type == "emotion" ?
                                      <>
                                        <Typography
                                          align="left"
                                          sx={{ marginLeft: 2, fontSize: "15px", fontWeight: "bold" }}
                                        >
                                          Emotion
                                        </Typography>
                                        <Box sx={{
                                          display: "flex",
                                          height: { xs: "65vh", sm: "90%" },
                                          width: "98%",
                                          justifyContent: 'center',
                                          flexDirection: 'column'
                                        }}>
                                          <StressChart stressChart={emotionCharts} page="Summary" />
                                        </Box></> :
                                      type == smokingData?.graphLabel && <SmokingChart
                                        smokingChartData={smokingChartData?.answers}
                                        page="Summary"
                                        title={smokingData?.graphLabel}
                                        idealValue={smokingData?.recommendation}
                                      />
            }
          </Box>
        </Modal>
      </Paper>
    </Box>
  );
};

export default SummaryPage;
